// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { OrderModel, OrderSettingsModel } from "@insite/client-framework/Types/ApiModels";
import OrderDetailPageTypeLink from "@insite/content-library/Components/OrderDetailPageTypeLink";
import { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { DataTableCell, DataTableRow } from "@insite/mobius/index";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    language: state.context.session.language,
});

interface OwnProps {
    order: OrderModel;
    orderSettings: OrderSettingsModel;
    extendedStyles?: OrderSummaryCardStyles;
    // XNG-Changes
    showOrderDate?: boolean;
    showOrderNumber?: boolean;
    showPONumber?: boolean;
    showStatus?: boolean;
    showOrderTotal?: boolean;
    // XNG-Changes: TPS-1112
    showOrderType?: boolean;
    showOrderedBy?: boolean;
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

export interface OrderSummaryCardStyles {
    orderSummaryCardGridItem?: GridItemProps;
    container?: GridContainerProps;
    pageTypeLinkGridItem?: GridItemProps;
    orderNumberGridItem?: GridItemProps;
    orderNumberHeadingAndText?: SmallHeadingAndTextStyles;
    statusGridItem?: GridItemProps;
    statusHeadingAndText?: SmallHeadingAndTextStyles;
    webOrderNumberGridItem?: GridItemProps;
    webOrderNumberHeadingAndText?: SmallHeadingAndTextStyles;
    totalGridItem?: GridItemProps;
    totalHeadingAndText?: SmallHeadingAndTextStyles;
    // XNG-Changes
    dataTableRow?: DataTableRowProps;
    dataTableCell?: DataTableCellProps;
}

export const orderSummaryCardStyles: OrderSummaryCardStyles = {
    orderSummaryCardGridItem: {
        width: 12,
        css: css`
            padding: 0 20px;
            max-width: 500px;
        `,
    },
    container: { gap: 5 },
    pageTypeLinkGridItem: { width: 12 },
    orderNumberGridItem: { width: 6 },
    statusGridItem: { width: 6 },
    webOrderNumberGridItem: { width: 6 },
    totalGridItem: { width: 6 },
};

const OrderSummaryCard: FC<Props> = ({
    language,
    order,
    orderSettings,
    extendedStyles,
    showOrderDate,
    showOrderNumber,
    showPONumber,
    showStatus,
    showOrderTotal,
    // XNG-Changes: TPS-1112
    showOrderType,
    showOrderedBy,
}) => {
    const [styles] = React.useState(() => mergeToNew(orderSummaryCardStyles, extendedStyles));

    // XNG-Chagnes
    if (!order) {
        return null;
    }

    const orderDateDisplay = getLocalizedDateTime({
        dateTime: order.orderDate,
        language,
        options: {
            year: "numeric",
            month: "numeric",
            day: "numeric",
        },
    });
    // XNG-Changes: TPS-938 Hide Opti Web Order Number
    const orderNumber = order.erpOrderNumber || String(siteMessage("Custom_WebOrder_Pending"));
    const linkOrderNumber = order.erpOrderNumber || order.webOrderNumber;

    // XNG-Changes: Change Strucuture to DataTable
    return (
        <DataTableRow {...styles.dataTableRow}>
            {showOrderDate && (
                <DataTableCell {...styles.dataTableCell}>
                    <OrderDetailPageTypeLink title={orderDateDisplay} orderNumber={linkOrderNumber} />
                </DataTableCell>
            )}
            {showOrderNumber && <DataTableCell {...styles.dataTableCell}>{orderNumber}</DataTableCell>}
            {/* XNG-Changes: TPS-1112 */}
            {showOrderType && (
                <DataTableCell {...styles.dataTableCell}>{order.properties?.orderType || ""}</DataTableCell>
            )}
            {showPONumber && <DataTableCell {...styles.dataTableCell}>{order.customerPO}</DataTableCell>}
            {/* XNG-Changes: TPS-1112 */}
            {showOrderedBy && (
                <DataTableCell {...styles.dataTableCell}>{order.properties?.orderedBy || ""}</DataTableCell>
            )}
            {showStatus && <DataTableCell {...styles.dataTableCell}>{order.statusDisplay}</DataTableCell>}
            {showOrderTotal && <DataTableCell {...styles.dataTableCell}>{order.orderTotalDisplay}</DataTableCell>}
        </DataTableRow>
    );
};

export default connect(mapStateToProps)(OrderSummaryCard);
